// FILE: client/src/pages/secure/IndependentContractorAgreementPage.js
// (This file is updated to fix the runtime error)

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApiClient } from '../../hooks/useApiClient';

const IndependentContractorAgreementPage = () => {
    const [agents, setAgents] = useState([]);
    const [formData, setFormData] = useState({
        agent_id: '',
        agreement_date: new Date().toISOString().split('T')[0],
        compensation_plan: '',
        termination_clause_days: '30',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [generatedFile, setGeneratedFile] = useState(null);
    const apiClient = useApiClient();

    useEffect(() => {
        // Fetch agents to populate the dropdown
        apiClient.get('/api/agents').then(response => {
            // **FIX**: The API client returns the data directly, not in a .data property.
            // If the response is not an array, default to an empty one to prevent errors.
            if (Array.isArray(response)) {
                setAgents(response);
            } else {
                setAgents([]);
                console.error("API response for agents was not an array:", response);
            }
        }).catch(err => {
            console.error("Failed to fetch agents", err)
            setAgents([]); // Also set to empty array on error
        });
    }, [apiClient]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setGeneratedFile(null);
        try {
            const response = await apiClient.post('/api/forms/generate-ica', formData, { responseType: 'blob' });
            
            // Create a URL for the blob
            const file = new Blob([response.data], { type: 'application/pdf' });
            const fileURL = URL.createObjectURL(file);
            setGeneratedFile(fileURL);

        } catch (error) {
            console.error("Failed to generate agreement", error);
            alert(error.response?.data?.message || 'An error occurred.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="p-4 md:p-8">
            <Link to="/dashboard/forms" className="text-indigo-600 hover:underline mb-6 inline-block">&larr; Back to Forms Portal</Link>
            <div className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-lg">
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Independent Contractor Agreement</h1>
                <p className="text-slate-500 mb-6">Fill out the details below to generate a new agreement.</p>

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label htmlFor="agent_id" className="block text-sm font-medium text-gray-700">Select Agent</label>
                        <select id="agent_id" name="agent_id" value={formData.agent_id} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm">
                            <option value="">-- Choose an agent --</option>
                            {/* This map function was causing the error */}
                            {agents.map(agent => (
                                <option key={agent.agent_id} value={agent.agent_id}>{agent.first_name} {agent.last_name} (ID: {agent.agent_id})</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label htmlFor="agreement_date" className="block text-sm font-medium text-gray-700">Agreement Date</label>
                        <input type="date" id="agreement_date" name="agreement_date" value={formData.agreement_date} onChange={handleChange} required className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm" />
                    </div>

                    <div>
                        <label htmlFor="compensation_plan" className="block text-sm font-medium text-gray-700">Compensation Plan Details</label>
                        <textarea id="compensation_plan" name="compensation_plan" rows="4" value={formData.compensation_plan} onChange={handleChange} required placeholder="e.g., 70/30 split on all residential sales commissions..." className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm"></textarea>
                    </div>
                    
                    <div className="flex justify-end">
                        <button type="submit" disabled={isSubmitting} className="px-6 py-2 bg-slate-800 text-white rounded-md disabled:bg-indigo-400">
                            {isSubmitting ? 'Generating...' : 'Generate Agreement PDF'}
                        </button>
                    </div>
                </form>

                {generatedFile && (
                    <div className="mt-8 border-t pt-6">
                        <h3 className="text-lg font-bold">Agreement Generated Successfully</h3>
                        <p className="mb-4">You can now download the PDF. In Phase 2, this would be sent for e-signature.</p>
                        <a href={generatedFile} download={`ICA_${formData.agent_id}_${formData.agreement_date}.pdf`} className="inline-block px-6 py-2 bg-green-600 text-white rounded-md">
                            Download PDF
                        </a>
                        <div className="mt-4 h-96">
                           <iframe src={generatedFile} width="100%" height="100%" title="Generated Agreement Preview"></iframe>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default IndependentContractorAgreementPage;
